import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {Configuration} from "../../assets/Configuration";
import { HttpClient, HttpParams } from "@angular/common/http";
import {TrainingSummaryModel} from "../models/training/training-summary.model";
import {TrainingFullModel} from "../models/training/training-full.model";
import {TrainingCreationAndUpdateModel} from "../models/training/training-creation-and-update.model";
import {RawFileModel} from "../models/raw-file-model";
import {DownloadService} from "./download.service";
import {TrainingCancelModel} from "../models/training/training-cancel.model";
import {TrainingCompleteModel} from "../models/training/training-complete.model";
import {TrainingFeedback} from '../models/training/training-feedback.model';
import {TrainingMeetingAttendeePersonModel} from "../models/training/training-meeting-attendee-person.model";
import { TrainingMemberModel } from '../models/training/training-member.model';
import { TrainingMemberAttendeeModel } from '../models/training/training-member-attendee.model';
import { TrainingChannelStatisticsModel } from '../models/training/training-channel-statistics.model';
import { TrainingManagementCountsModel } from '../models/training/training-management-counts.model';
import { TrainingManageSummaryModel } from '../models/training/training-manage-summary.model';
import { EventMemberModel } from '../models/events/event-member.model';
import { TrainingFullViewModel } from '../models/training/training-full-view.model';
import { EventParticipationAnswerCreationModel } from '../models/event-participations/event-participation-answer-creation.model';
import { TrainingStartDateChangedNotifyModel } from '../models/training/training-start-date-changed-notify.model';
import { TrainingIddTimeModel } from '../models/training/training-idd-time.model';
import { StandardTrainingCategoryOverviewModel } from '../models/standard-training-category/standard-training-category-overview.model';

@Injectable({
    providedIn: 'root'
})
export class TrainingService {

    constructor(private httpClient: HttpClient, private downloadService: DownloadService) {
    }

    public getAllTrainings(status: number | null = null, year: number | null = null): Observable<TrainingSummaryModel[]> {
        let params = new HttpParams();

        if (status !== null) params = params.set('status', status.toString());
        if (year !== null) params = params.set('year', year.toString());

        return this.httpClient.get<TrainingSummaryModel[]>(
            Configuration.getApiBaseUrl() + "/training/",
            { params }
        );
    }

    public getOpenTrainingsForManagement(): Observable<TrainingManageSummaryModel[]> {
        return this.httpClient.get<TrainingManageSummaryModel[]>(
            Configuration.getApiBaseUrl() + "/training/management/open"
        );
    }

    public getToBeClosedTrainingsForManagement(): Observable<TrainingManageSummaryModel[]> {
        return this.httpClient.get<TrainingManageSummaryModel[]>(
            Configuration.getApiBaseUrl() + "/training/management/to-be-closed"
        );
    }

    public getCompletedTrainingsForManagement(year: number): Observable<TrainingManageSummaryModel[]> {
        const params = new HttpParams()
            .set('year', year.toString())

        return this.httpClient.get<TrainingManageSummaryModel[]>(
            Configuration.getApiBaseUrl() + "/training/management/completed",
            { params }
        );
    }


    public getTrainingCountsForManagement(year: number): Observable<TrainingManagementCountsModel> {
        const params = new HttpParams()
            .set('selectedYear', year.toString())

        return this.httpClient.get<TrainingManagementCountsModel>(
            Configuration.getApiBaseUrl() + "/training/management/counts",
            { params }
        );
    }

    public getTraining(id: string): Observable<TrainingFullModel> {
        return this.httpClient.get<TrainingFullModel>(Configuration.getApiBaseUrl() + "/training/" + id);
    }

    public getTrainingView(id: string): Observable<TrainingFullViewModel> {
        return this.httpClient.get<TrainingFullViewModel>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/view"
        );
    }

    public getAcceptedAndOpenTrainings(personId: string): Observable<TrainingSummaryModel[]> {
        const params = new HttpParams()
            .set('personId', personId);
      
        return this.httpClient.get<TrainingSummaryModel[]>(
            Configuration.getApiBaseUrl() + "/training/accepted-open",
            { params: params }
        );
    }

    public addTraining(model: TrainingCreationAndUpdateModel): Observable<TrainingFullModel> {
        return this.httpClient.post<TrainingFullModel>(Configuration.getApiBaseUrl() + "/training/", model);
    }

    public updateTraining(id: string, model: TrainingCreationAndUpdateModel): Observable<TrainingFullModel> {
        return this.httpClient.post<TrainingFullModel>(Configuration.getApiBaseUrl() + "/training/" + id, model);
    }

    public getTrainingMemberAttendees(id: string): Observable<TrainingMemberAttendeeModel[]> {
        return this.httpClient.get<TrainingMemberAttendeeModel[]>(Configuration.getApiBaseUrl() + "/training/" + id + "/member-attendees");
    }

    public getEventMembers(id: string): Observable<EventMemberModel[]> {
        return this.httpClient.get<EventMemberModel[]>(Configuration.getApiBaseUrl() + "/training/" + id + "/members");
    }

    public getIcs(id: string): Observable<RawFileModel> {
        return this.httpClient.get<RawFileModel>(Configuration.getApiBaseUrl() + "/training/" + id + "/ics");
    }

    public deleteTraining(id: string): Observable<void> {
        return this.httpClient.delete<void>(Configuration.getApiBaseUrl() + "/training/" + id);
    }

    getMemberListPdf(id: string): Observable<RawFileModel> {
        return this.httpClient.get<RawFileModel>(Configuration.getApiBaseUrl() + "/training/" + id + "/member-list-pdf");
    }

    public downloadESignatureQRCode(id: string): Observable<string|null> {
        return this.httpClient.get<string|null>(
            Configuration.getApiBaseUrl() + "/training/download-esignature-qr-code/" + id, {
                responseType: 'text' as 'json'
            }
        );
    }

    public downloadFeedbackQRCode(id: string): Observable<string|null> {
        return this.httpClient.get<string|null>(
            Configuration.getApiBaseUrl() + "/training/download-feedback-qr-code/" + id, {
                responseType: 'text' as 'json'
            }
        );
    }

    public cancelTraining(id: string, trainingCancelModel: TrainingCancelModel): Observable<void> {
        return this.httpClient.post<void>(Configuration.getApiBaseUrl() + "/training/" + id + "/cancel", trainingCancelModel);
    }

    public notifyTrainingStartDateChanged(
        id: string, 
        model: TrainingStartDateChangedNotifyModel
    ): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/notify-start-date-changed",
            model
        );
    }

    public completeTraining(id: string, trainingCompleteModel: TrainingCompleteModel): Observable<void> {
        return this.httpClient.post<void>(Configuration.getApiBaseUrl() + "/training/" + id + "/complete", trainingCompleteModel);
    }

    public acceptTraining(
        id: string, 
        models: EventParticipationAnswerCreationModel[] | null
    ): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/accept",
            models
        );
    }

    public declineTraining(id: string): Observable<void> {
        return this.httpClient.post<void>(Configuration.getApiBaseUrl() + "/training/" + id + "/decline", null);
    }

    public getTrainingFeedback(id: string): Observable<TrainingFeedback> {
        return this.httpClient.get<TrainingFeedback>(Configuration.getApiBaseUrl() + "/SurveyResult/" + id);
    }

    public getTrainingMeetingAttendees(id: string): Observable<TrainingMeetingAttendeePersonModel[]> {
        return this.httpClient.get<TrainingMeetingAttendeePersonModel[]>(Configuration.getApiBaseUrl() + "/training/" + id + "/meeting-attendees");
    }

     public getTrainingChannelsStatistics(id: string): Observable<TrainingChannelStatisticsModel[]> {
        return this.httpClient.get<TrainingChannelStatisticsModel[]>(Configuration.getApiBaseUrl() + "/training/" + id + "/channel-statistics");
    }

    public getTrainingMemberStatus(id: string): Observable<TrainingMemberModel | null> {
        return this.httpClient.get<TrainingMemberModel | null>(Configuration.getApiBaseUrl() + "/training/" + id + "/member");
    }

    public deleteTrainingMember(id: string, personId: string): Observable<TrainingMemberModel> {
        return this.httpClient.delete<TrainingMemberModel>(Configuration.getApiBaseUrl() + "/training/" + id + "/member/" + personId);
    }

    public approveMemberRequest(id: string, personId: string): Observable<void> {
        return this.httpClient.post<void>(Configuration.getApiBaseUrl() + "/training/" + id + "/approve-request/" + personId, null);
    }

    public declineMemberRequest(id: string, personId: string): Observable<void> {
        return this.httpClient.post<void>(Configuration.getApiBaseUrl() + "/training/" + id + "/decline-request/" + personId, null);
    }

    public approveTrainingQueueRequest(id: string, personId: string): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/queue/" + personId + "/approve",
            null
        );
    }

    public declineTrainingQueueRequest(id: string, personId: string): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/queue/" + personId + "/decline",
            null
        );    
    }

     public enqueueTraining(
        id: string, 
        models: EventParticipationAnswerCreationModel[] | null
    ): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/enqueue",
            models
        );
    }

    public dequeueTraining(id: string): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/dequeue",
            null
        );    
    }

    public declineTrainerInvitation(id: string): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/decline-trainer-invitation",
            null
        );
    }

    public hasBrokerIddNumber(id: string): Observable<boolean> {
        return this.httpClient.get<boolean>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/broker-idd"
        );
    }

     public setBrokerIddNumber(id: string): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + id + "/broker-idd",
            null
        );
    }

    public addAdditionalParticipants(
        trainingId: string,
        participantIds: string[]
    ): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + trainingId + "/additional-participants",
            participantIds
        );
    }

    public removeAdditionalParticipant(
        trainingId: string,
        personId: string
    ): Observable<void> {
        return this.httpClient.delete<void>(
            Configuration.getApiBaseUrl() +  
                "/training/" + trainingId + 
                "/additional-participants/" + personId
        );
    }

    public updateIddTime(
        trainingId: string,
        models: TrainingIddTimeModel[]
    ): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + "/training/" + trainingId + "/idd-time",
            models
        );
    }

    public getUsedStandardTrainingCategories(
    ): Observable<StandardTrainingCategoryOverviewModel[]> {
        return this.httpClient.get<StandardTrainingCategoryOverviewModel[]>(
            Configuration.getApiBaseUrl() + "/training/used-standard-categories"
        );
    }
}
